// App-level artifact store. At ~100 items we load ALL artifacts in one GET and
// partition by status client-side (DESIGN §6: fewer round-trips, instant counts
// + pane switches). Panes read their slice and call refresh() after mutations.

import { useCallback, useEffect, useMemo, useState } from "react";
import { listArtifacts } from "./api";
import type { Artifact, ArtifactStatus } from "./types";

export interface ArtifactStore {
  all: Artifact[];
  byStatus: Record<ArtifactStatus, Artifact[]>;
  counts: Record<ArtifactStatus, number>;
  /** Distinct tour slugs across all loaded artifacts (for one-tap reuse). */
  tourTags: string[];
  loading: boolean;
  error: string | null;
  /** Re-fetch everything from the API. */
  refresh: () => Promise<void>;
  /** Optimistically replace/insert one artifact in the local store. */
  upsert: (a: Artifact) => void;
  /** Optimistically remove one artifact. */
  remove: (id: string) => void;
}

const EMPTY_BY_STATUS: Record<ArtifactStatus, Artifact[]> = {
  pending: [],
  needs_review: [],
  confirmed: [],
};

export function useArtifacts(): ArtifactStore {
  const [all, setAll] = useState<Artifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await listArtifacts("all");
      // Newest first by updatedAt (GSI1 SK).
      items.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
      setAll(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load artifacts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const upsert = useCallback((a: Artifact) => {
    setAll((prev) => {
      const idx = prev.findIndex((x) => x.id === a.id);
      const next = idx >= 0 ? prev.map((x) => (x.id === a.id ? a : x)) : [a, ...prev];
      next.sort((x, y) => (x.updatedAt < y.updatedAt ? 1 : -1));
      return next;
    });
  }, []);

  const remove = useCallback((id: string) => {
    setAll((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const byStatus = useMemo(() => {
    const map: Record<ArtifactStatus, Artifact[]> = {
      pending: [],
      needs_review: [],
      confirmed: [],
    };
    for (const a of all) {
      // status is always one of the three; guard defensively anyway.
      (map[a.status] ?? (map[a.status] = [])).push(a);
    }
    return map;
  }, [all]);

  const counts = useMemo(
    () => ({
      pending: byStatus.pending.length,
      needs_review: byStatus.needs_review.length,
      confirmed: byStatus.confirmed.length,
    }),
    [byStatus],
  );

  // Distinct tour slugs across every loaded artifact, sorted for stable display.
  // Source for the one-tap "reuse an existing tour" chips (DESIGN §9).
  const tourTags = useMemo(() => {
    const seen = new Set<string>();
    for (const a of all) {
      for (const t of a.tourTags ?? []) {
        if (t) seen.add(t);
      }
    }
    return [...seen].sort();
  }, [all]);

  return {
    all,
    byStatus: all.length ? byStatus : EMPTY_BY_STATUS,
    counts,
    tourTags,
    loading,
    error,
    refresh,
    upsert,
    remove,
  };
}
